/**
 * Dialog for adding a new DID to the inventory.
 */
import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Stack,
} from "@mui/material";
import { didsApi } from "../../api/dids";
import type { DIDCreatePayload } from "../../types/did";

interface AddDIDDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (message: string) => void;
}

/** Add-DID form dialog. */
export default function AddDIDDialog({ open, onClose, onCreated }: AddDIDDialogProps) {
  const [form, setForm] = useState<DIDCreatePayload>({
    phone_number: "",
    area_code: "",
    country_code: "1",
    monthly_cost: 0,
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    setError("");
    setForm({ phone_number: "", area_code: "", country_code: "1", monthly_cost: 0 });
    onClose();
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.phone_number.trim() || !form.area_code.trim()) {
      setError("Phone number and area code are required.");
      return;
    }
    if (form.monthly_cost < 0) {
      setError("Monthly cost cannot be negative.");
      return;
    }
    setSubmitting(true);
    try {
      await didsApi.create({
        ...form,
        phone_number: form.phone_number.trim(),
        area_code: form.area_code.trim(),
      });
      onCreated(`DID ${form.phone_number.trim()} added to inventory.`);
      handleClose();
    } catch (err: unknown) {
      const detail =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (err as any)?.response?.data?.detail || "Failed to create DID.";
      setError(String(detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add DID</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Phone number (E.164)"
            value={form.phone_number}
            onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
            placeholder="+12125550123"
            required
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Area code"
              value={form.area_code}
              onChange={(e) => setForm({ ...form, area_code: e.target.value })}
              placeholder="212"
              required
              fullWidth
            />
            <TextField
              label="Country code"
              value={form.country_code}
              onChange={(e) => setForm({ ...form, country_code: e.target.value })}
              fullWidth
            />
          </Stack>
          <TextField
            label="Monthly cost"
            type="number"
            value={form.monthly_cost}
            onChange={(e) =>
              setForm({ ...form, monthly_cost: Number(e.target.value) })
            }
            inputProps={{ min: 0, step: 0.01 }}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? "Adding…" : "Add DID"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
